import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCalendar, FiTag, FiUser } from 'react-icons/fi';

import { type ActionItemColumn, type GroupByOption } from './action-item-types';
import { ACTIONS_COLUMN_WIDTH, getColumnWidth } from './action-items-table-utils';

interface ActionItemTableHeaderProps {
  columns: ActionItemColumn[];
  groupBy: GroupByOption;
}

const getColumnIcon = (col: ActionItemColumn) => {
  switch (col) {
    case 'person':
      return <FiUser className="w-4 h-4" />;
    case 'deadline':
      return <FiCalendar className="w-4 h-4" />;
    case 'topic':
      return <FiTag className="w-4 h-4" />;
    default:
      return null;
  }
};

export const ActionItemTableHeader: FC<ActionItemTableHeaderProps> = ({ columns, groupBy }) => {
  const { t } = useTranslation();

  const visibleColumns = useMemo(
    () => columns.filter((col) => col !== groupBy),
    [columns, groupBy],
  );

  const getColumnLabel = (col: ActionItemColumn): string => {
    switch (col) {
      case 'title':
        return t('meeting-secretary.action-items.column-title');
      case 'person':
        return t('meeting-secretary.action-items.column-person');
      case 'deadline':
        return t('meeting-secretary.action-items.column-deadline');
      case 'topic':
        return t('meeting-secretary.action-items.column-topic');
      case 'status':
        return t('meeting-secretary.action-items.column-status');
      case 'meeting':
        return t('meeting-secretary.action-items.column-meeting');
      default:
        return '';
    }
  };

  return (
    <div className="flex gap-4 px-4 py-3 border-b border-gray-200 bg-gray-50 text-sm font-medium text-gray-600">
      {visibleColumns.map((col) => {
        const icon = getColumnIcon(col);
        return (
          <div key={col} className={`${getColumnWidth(col)} flex items-center gap-1.5`}>
            {icon}
            {getColumnLabel(col)}
          </div>
        );
      })}
      <div className={ACTIONS_COLUMN_WIDTH} />
    </div>
  );
};
